/**
 * @file: useConversationPresence.ts
 *
 * The "this conversation is on screen" effects — nothing about loading
 * messages (that lives in `useMessageList`):
 *   - mark the thread read (optimistically + on the server) while viewing,
 *   - publish the active room to the app-wide session.
 *
 * Both go through the app-wide session: `setActiveRoomId`, and
 * `threadReadLocally$` — the intent bus a mounted thread list subscribes to so
 * its row's badge clears immediately. The conversation lives in a different
 * tree from the thread store (a root-stack chat flow / a floating window), so
 * it announces the read rather than calling the store; with no list mounted
 * nothing listens and only the server read fires.
 */

import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { useServiceFactory } from '@euler/services/factory';
import { idEquals, onErrorIgnore } from '@euler/utils';
import { useEffect } from 'react';
import { ChatConversationHandle, isBaked } from '../handle';

export function useConversationPresence(
  conversationHandle: ChatConversationHandle,
) {
  const { chatService } = useServiceFactory();
  const { me, wsClient, activeRoomId$, setActiveRoomId, threadReadLocally$ } =
    useChatSession();
  const roomId = isBaked(conversationHandle) ? conversationHandle.roomId : null;
  const threadId = isBaked(conversationHandle)
    ? conversationHandle.threadId
    : null;

  useEffect(() => {
    if (threadId) {
      threadReadLocally$.next(threadId);
      chatService.markThreadRead(threadId).catch(onErrorIgnore);
    }
  }, [chatService, threadId, threadReadLocally$]);

  // While the conversation is on screen, a message arriving from someone else
  // is read the moment it lands — mark the thread read on the server so the
  // unread doesn't resurface on the next API reconcile or another device. The
  // inbox reducer already suppresses the optimistic bump for the active room,
  // so this is purely server-side persistence; our own echo needs no mark.
  useEffect(() => {
    if (!roomId || !threadId) return;
    const sub = wsClient.onRoomEvent(roomId).subscribe(event => {
      if (event.kind !== 'message.new') return;
      const isOwnEcho =
        event.type === 'user' &&
        idEquals(event.platformUserId, me.platformUserId);
      if (!isOwnEcho) chatService.markThreadRead(threadId).catch(onErrorIgnore);
    });
    return () => sub.unsubscribe();
  }, [roomId, threadId, wsClient, me, chatService]);

  useEffect(() => {
    if (!roomId) return;
    setActiveRoomId(roomId);
    // On a desktop room→room swap the OUTgoing pane's cleanup runs after the
    // INcoming pane's effect, so an unconditional null would wipe the room
    // that just took over. Only clear if we're still the active room.
    return () => {
      if (activeRoomId$.getValue() === roomId) setActiveRoomId(null);
    };
  }, [roomId, activeRoomId$, setActiveRoomId]);
}
